// Copyright (c) 2024 by Audere

import { Backdrop, Box, CircularProgress } from "@mui/material";
import {
  CredentialResponse,
  GoogleLogin,
  GoogleOAuthProvider,
} from "@react-oauth/google";
import { useCallback, useEffect, useState } from "react";

import {
  State as AuthState,
  GroupRole,
  PatientGroupRoles,
  selectCurrentPersistentGroupRole
} from "../features/slices/authSlice";
import { useLazyGetRolesQuery } from "../features/api/labelApiSlice";
import { createStyleSheet } from "../utils/styles";
import { getErrorText } from "../utils/errors";
import { useLazyOpenIdAuthenticateQuery } from "../features/api/authApiSlice";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const LoginButton = (props: {
  googleClientId: string;
  onAuthSuccess: (authData: AuthState) => void;
  onAuthError: (errorMsg: string) => void;
}) => {
  const { googleClientId, onAuthSuccess, onAuthError } = props;
  const persistentGroupRole = useSelector(selectCurrentPersistentGroupRole);
  const { t } = useTranslation();

  // Component state
  const [isTryingToLogin, setIsTryingToLogin] = useState(false);

  // Queries/mutations
  const [openIdAuthenticate, openIdAuthenticateResult] =
    useLazyOpenIdAuthenticateQuery();
  const [getRoles, getRolesResult] = useLazyGetRolesQuery();
  const {
    data: authData,
    error: authError,
    isError: isAuthError,
    isSuccess: isAuthSuccess,
  } = openIdAuthenticateResult;
  const {
    data: rolesData,
    error: rolesError,
    isError: isRolesError,
    isSuccess: isRolesSuccess,
  } = getRolesResult;

  // Callbacks
  const loginSuccess = useCallback(
    (credentialResponse: CredentialResponse) => {
      if (credentialResponse.credential) {
        openIdAuthenticate(credentialResponse.credential)
          .unwrap()
          .catch(() => {
            setIsTryingToLogin(false);
            onAuthError("Authentication failed!");
          });
      } else {
        setIsTryingToLogin(false);
        onAuthError("Login error!");
      }
    },
    [openIdAuthenticate, onAuthError]
  );

  const loginError = useCallback(() => {
    setIsTryingToLogin(false);
    onAuthError("Login error!");
  }, [onAuthError]);

  const handleLogin = useCallback(() => {
    setIsTryingToLogin(true);
  }, []);

  // Reactive code
  useEffect(() => {
    if (isAuthSuccess) {
      const token = authData.token || "";
      getRoles(token)
        .unwrap()
        .catch(() => {
          setIsTryingToLogin(false);
        });
      if (isRolesSuccess) {
        const patientGroupRoles = Object.keys(rolesData.groups)
          .reduce((acc: PatientGroupRoles, group: string) => {
            acc[group] = rolesData.groups[group].roles;
            return acc;
          }, {});

        if (Object.keys(patientGroupRoles).length === 0) {
          throw new Error(t("error.toolAccessUnauthorized"),
            { cause: t("error.401") }
          );
        }

        let newPersistentGroupRole: GroupRole;
        const currentGroupRole = persistentGroupRole?.[authData.email];
        if (
          currentGroupRole &&
          currentGroupRole.group in patientGroupRoles &&
          patientGroupRoles[currentGroupRole.group].includes(currentGroupRole.role)) {
          newPersistentGroupRole = currentGroupRole;
        } else {
          const firstGroup = Object.keys(patientGroupRoles)[0];
          newPersistentGroupRole = { group: firstGroup, role: patientGroupRoles[firstGroup][0] };
        }
        setIsTryingToLogin(false);
        onAuthSuccess({
          ...authData,
          patientGroupRoles,
          persistentGroupRole: { [authData.email]: newPersistentGroupRole },
        });
      } else if (isRolesError) {
        setIsTryingToLogin(false);
        onAuthError(getErrorText(rolesError));
      }
    } else if (isAuthError) {
      setIsTryingToLogin(false);
      onAuthError(getErrorText(authError));
    }
  }, [
    isAuthSuccess,
    authData,
    getRoles,
    isRolesSuccess,
    rolesData,
    onAuthSuccess,
    isRolesError,
    rolesError,
    onAuthError,
    isAuthError,
    authError,
    persistentGroupRole,
    t,
  ]);

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <Box>
        <GoogleLogin
          onSuccess={loginSuccess}
          onError={loginError}
          click_listener={handleLogin}
          size="large"
          theme="filled_blue"
        />
      </Box>
      <Backdrop sx={styles.backdrop} open={isTryingToLogin}>
        <CircularProgress color="inherit" aria-label="Logging in" />
      </Backdrop>
    </GoogleOAuthProvider>
  );
};

const styles = createStyleSheet({
  backdrop: {
    color: "white",
    zIndex: (theme) => theme.zIndex.drawer + 1,
  },
});

export default LoginButton;
